import { useState, useEffect, useRef } from 'react'

// Import images locally
import heroImg from '../assets/images/hero.jpg'
import heroAltImg from '../assets/images/hero-alt.jpg'
import softwareImg from '../assets/images/software_computer.avif' // '../assets/images/software.jpg'
import medicalImg from '../assets/images/medical.jpg'
import lifeScienceImg from '../assets/images/life-science.jpg'
import dataImg from '../assets/images/data2.jpg'
import medicalDevicesImg from '../assets/images/medical-devices.jpg'
import instrumentsImg from '../assets/images/instruments.jpg'
import biotechImg from '../assets/images/biotech.jpg'
import medicalDeviceHologram from '../assets/images/medical_hologram_interaction.png'
import imageProcessingImg from '../assets/images/image_processing.jpg'
import veedaLogo from '../assets/images/veeda-leaf-square.svg'

// Logo component
const Logo = ({ className = '' }) => (
  <div className={`flex items-center ${className}`}>
    <img src={veedaLogo} alt="Veeda Software" className="w-12 h-12" />
    <span className="text-3xl font-light tracking-wide leading-tight">
      <span className="font-semibold">Veeda</span>
      <span className="text-teal-400 ml-1">Software</span>
    </span>
  </div>
)

// Local images
const images = {
  hero: heroImg, // Lab tech
  heroAlt: heroAltImg, // Circuit board
  software: softwareImg, // Code/programming
  medical: medicalImg, // Medical device
  lifeScience: lifeScienceImg, // Lab microscope
  data: dataImg, // Data dashboard
  medicalDevices: medicalDevicesImg, // Medical equipment
  instruments: instrumentsImg, // Lab instruments
  biotech: biotechImg, // DNA/biotech
  medicalDeviceHologram: medicalDeviceHologram, // Medical device hologram
  imageProcessing: imageProcessingImg, // Image processing
}

// Intersection Observer hook for scroll animations
// Starts visible (for prerendering/SEO), then on mount checks viewport position:
// - already in viewport → stays visible
// - below viewport → hides, then reveals on scroll
const useInView = (threshold = 0.1) => {
  const ref = useRef(null)
  const [isInView, setIsInView] = useState(true)

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0
      if (inViewport) return
    }

    setIsInView(false)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
        }
      },
      { threshold }
    )

    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [threshold])

  return [ref, isInView]
}

// Rotating text component for expertise badge
const RotatingText = ({ items, interval = 3500 }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setInterval(() => {
      setIsVisible(false)
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % items.length)
        setIsVisible(true)
      }, 500)
    }, interval)

    return () => clearInterval(timer)
  }, [items.length, interval])

  return (
    <div className="h-5 relative">
      <span
        className={`transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      >
        {items[currentIndex]}
      </span>
    </div>
  )
}

// Service Card with Image Background
const ServiceCard = ({ image, title, description, delay = 0 }) => {
  const [ref, isInView] = useInView(0.2)

  return (
    <div
      ref={ref}
      className={`group relative h-80 rounded-2xl overflow-hidden cursor-default transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{ backgroundImage: `url(${image})` }}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/20 group-hover:via-slate-900/70 transition-all duration-500" />

      {/* Content */}
      <div className="absolute inset-0 p-8 flex flex-col justify-end">
        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors duration-300">
          {title}
        </h3>
        <p className="text-slate-300 leading-relaxed opacity-90">{description}</p>

        {/* Hover accent line */}
        <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-cyan-500 to-teal-500 w-0 group-hover:w-full transition-all duration-500" />
      </div>
    </div>
  )
}

// Industry Card with Image
const IndustryCard = ({ image, title, description, items, delay = 0 }) => {
  const [ref, isInView] = useInView(0.2)

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl overflow-hidden transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
          style={{ backgroundImage: `url(${image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative bg-slate-800/80 backdrop-blur-sm p-6 border border-slate-700/50 border-t-0 rounded-b-2xl">
        <h3 className="text-xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">
          {title}
        </h3>
        {description && <p className="text-slate-400 mb-4">{description}</p>}
        {items && items.length > 0 && (
          <ul className="text-slate-400 space-y-2">
            {items.map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// Main App Component
export default function VeedaSoftware() {
  const [scrolled, setScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState('home')

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)

      const sections = ['home', 'services', 'industries', 'about', 'contact']
      for (const section of sections.reverse()) {
        const element = document.getElementById(section)
        if (element && window.scrollY >= element.offsetTop - 200) {
          setActiveSection(section)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://assets.calendly.com/assets/external/widget.js'
    script.async = true
    document.body.appendChild(script)

    const link = document.createElement('link')
    link.href = 'https://assets.calendly.com/assets/external/widget.css'
    link.rel = 'stylesheet'
    document.head.appendChild(link)

    return () => {
      document.body.removeChild(script)
      document.head.removeChild(link)
    }
  }, [])

  const openCalendly = () => {
    window.Calendly?.initPopupWidget({
      url: 'https://calendly.com/hendrickmelo/quick-meet',
    })
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const services = [
    {
      image: images.software,
      title: 'Firmware & Embedded Software',
      description:
        'From bare-metal sensor drivers to real-time acquisition. I work directly with your EEs and systems engineers to understand the hardware and build software that talks to it reliably.',
    },
    {
      image: images.medicalDeviceHologram,
      title: 'Desktop Applications & User Interfaces',
      description:
        'The software your users actually touch. Intuitive interfaces for acquisition, analysis, and visualization — designed around real workflows, not engineering convenience.',
    },
    {
      image: images.data,
      title: 'Cloud & Data Platforms',
      description:
        'When your instrument needs to connect — data pipelines, remote monitoring, analysis platforms. Scalable infrastructure that grows with your product.',
    },
    {
      image: images.imageProcessing,
      title: 'Signal & Image Processing',
      description:
        'Turning raw sensor data into meaningful results. From spectral analysis to medical imaging, I help you extract the insights your science depends on.',
    },
  ]

  const industries = [
    {
      image: images.medicalDevices,
      title: 'Medical Devices',
      description:
        'Diagnostics, imaging, therapeutic devices, wearables. I understand the regulatory landscape and build with compliance in mind from day one.',
    },
    {
      image: images.instruments,
      title: 'Scientific Instruments',
      description:
        'Analytical equipment, lab automation, precision sensors, measurement systems. Software that matches the rigor of your science.',
    },
    {
      image: images.biotech,
      title: 'Life Sciences',
      description:
        'Biotech platforms, research equipment, diagnostics solutions. From early R&D tools to commercial products.',
    },
  ]

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans antialiased overflow-x-hidden">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-slate-900/95 backdrop-blur-lg shadow-lg shadow-slate-900/50' : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Logo />
            <div className="hidden md:flex items-center gap-8">
              {['Services', 'Industries', 'About', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    activeSection === item.toLowerCase()
                      ? 'text-teal-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden sm:block bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-900 font-semibold px-5 py-2.5 rounded-lg hover:shadow-lg hover:shadow-teal-500/25 transition-all duration-300 hover:scale-105"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${images.heroAlt})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80" />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(6, 182, 212, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-8">
                <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                <span className="text-teal-400 text-sm font-medium">
                  Software Engineering Consulting
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                <span className="text-white">From Prototype</span>
                <br />
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  to Production
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed mb-10 max-w-xl">
                You&apos;ve proven what&apos;s possible — a new signal, a novel detector, a
                breakthrough your team brought out of the lab. Now you need to build something real.
              </p>
              <p className="text-xl text-slate-400 leading-relaxed mb-10 max-w-xl">
                I work across the full stack — firmware, desktop applications, cloud platforms — so
                you get one engineer instead of a fragmented team. From bench prototype to shippable
                instrument.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('services')}
                  className="group bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-900 font-semibold px-8 py-4 rounded-xl hover:shadow-xl hover:shadow-teal-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
                >
                  Explore Services
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="border border-slate-700 text-white font-semibold px-8 py-4 rounded-xl hover:border-teal-500/50 hover:bg-teal-500/5 transition-all duration-300"
                >
                  Learn More
                </button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap gap-6 mt-12 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="text-teal-400">✓</span> Medical & Scientific Instruments
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-teal-400">✓</span> Firmware to Cloud
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-teal-400">✓</span> Prototype to Production
                </div>
              </div>
            </div>

            {/* Hero Images */}
            <div className="hidden lg:block relative">
              <div className="relative h-[500px]">
                {/* Main floating image */}
                <div className="absolute top-0 right-0 w-80 h-64 rounded-2xl overflow-hidden shadow-2xl shadow-teal-900/30 border border-slate-700/50 animate-float">
                  <img
                    src={images.hero}
                    alt="Lab technology"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>

                {/* Secondary floating image */}
                <div className="absolute bottom-0 left-0 w-72 h-52 rounded-2xl overflow-hidden shadow-2xl shadow-teal-900/30 border border-slate-700/50 animate-float-reverse">
                  <img
                    src={images.medicalDeviceHologram}
                    alt="Medical device"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>

                {/* Decorative elements */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />

                {/* Stats card */}
                <div className="absolute bottom-20 right-0 bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl min-w-[250px]">
                  <div className="mono text-xs text-teal-400 mb-2">{'// expertise'}</div>
                  <div className="text-2xl font-bold text-white">20+ Years</div>
                  <div className="text-sm text-slate-400">
                    <RotatingText
                      items={[
                        'Medical & scientific instruments',
                        'Firmware to cloud',
                        'Lab to market',
                        'Startups to Fortune 500',
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results / Social Proof Section */}
      <section className="relative py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">12+</div>
              <div className="text-slate-400 text-sm">Years in Life Sciences</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">3</div>
              <div className="text-slate-400 text-sm">510(k) Cleared Devices</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">10+</div>
              <div className="text-slate-400 text-sm">Medical/Scientific Devices</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-teal-400 mb-2">2</div>
              <div className="text-slate-400 text-sm">Industry Awards (SLAS & R&D 100)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="relative py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-teal-400 font-medium text-sm tracking-wider uppercase mb-4 block">
              What I Do
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              One Engineer. Full Stack.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Everything your instrument needs — without the overhead of a team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                image={service.image}
                title={service.title}
                description={service.description}
                delay={index * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="relative py-32 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-teal-400 font-medium text-sm tracking-wider uppercase mb-4 block">
              Industries
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Deep Domain Experience
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              20+ years building software where precision matters.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <IndustryCard
                key={index}
                image={industry.image}
                title={industry.title}
                description={industry.description}
                items={industry.items}
                delay={index * 150}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Veeda Section */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-teal-400 font-medium text-sm tracking-wider uppercase mb-4 block">
              The Veeda Difference
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why Teams Choose Veeda
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Breadth Without the Overhead</h3>
              <p className="text-slate-400">
                Large firms bring 3-4 specialists — firmware, web, UI — who need to coordinate,
                hand off, and ramp up. I bring experience across all of it. Fewer seams, faster
                decisions, lower cost.
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">🤝</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Embedded in Your Team</h3>
              <p className="text-slate-400">
                I work directly with your engineers, scientists, and product owners. Same Slack,
                same standups, same commitment to your success — without the agency barrier.
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Prototype to Production Mindset
              </h3>
              <p className="text-slate-400">
                I&apos;ve taken instruments from first working demo to FDA clearance and commercial
                launch. I understand the path, the pace, and the pressure of aggressive timelines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-32 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-teal-400 font-medium text-sm tracking-wider uppercase mb-4 block">
                Your Partner
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Hendrick Melo</h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                20+ years building software for complex instruments — from scientific devices to
                medical platforms to biotech systems. I&apos;ve led software efforts from first
                prototype through regulatory clearance and commercial launch.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                I&apos;ve helped companies like Thermo Fisher, Volcano (now Philips), and multiple
                startups ship products that work. I was employee #1 at CytoTronics. From first line
                of code to award-winning product, I built and led the team behind the Pixel
                system&apos;s software and cloud platform — winner of both the{' '}
                <a
                  href="https://cytotronics.com/society-for-laboratory-automation-and-screening-slas-selects-cytotronics-pixel-system-for-2024-new-product-award/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-400 hover:text-teal-300 transition-colors"
                >
                  SLAS 2024 New Product Award
                </a>{' '}
                and the{' '}
                <a
                  href="https://cytotronics.com/cytotronics-pixel-system-receives-2024-rd-100-award/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-400 hover:text-teal-300 transition-colors"
                >
                  R&amp;D 100 Award
                </a>
                .
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {['Python', 'C/C++', 'Qt', 'React', 'AWS', 'Embedded', 'Real-time Systems', 'Image Processing', 'Linux/Windows/Mac', 'IEC 62304'].map(
                  (skill) => (
                    <span
                      key={skill}
                      className="bg-slate-800 border border-slate-700 px-4 py-2 rounded-lg text-sm text-slate-300 hover:border-teal-500/50 transition-colors"
                    >
                      {skill}
                    </span>
                  )
                )}
              </div>

              <a
                href="https://www.linkedin.com/in/hendrickmelo/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors font-medium"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                Connect on LinkedIn
              </a>
            </div>

            {/* HIDDEN: Photo placeholder - uncomment when photo is ready */}
            {false && (
              <div className="order-1 lg:order-2 relative">
                {/* Photo with decorative frame */}
                <div className="relative max-w-md mx-auto">
                  {/* Background accents */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-3xl transform rotate-3" />
                  <div className="absolute -inset-4 bg-gradient-to-tr from-slate-700/50 to-transparent rounded-3xl transform -rotate-2" />

                  {/* Main image container */}
                  <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl shadow-slate-900/50">
                    {/* PLACEHOLDER: Replace with actual photo */}
                    <div className="aspect-[4/5] bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="w-40 h-40 mx-auto bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-teal-500/30">
                          <span className="text-7xl font-bold text-white">HM</span>
                        </div>
                        <p className="text-slate-400 text-sm">
                          {/* Replace this placeholder with: */}
                          {/* <img src="your-photo.jpg" alt="Hendrick Melo" className="w-full h-full object-cover" /> */}
                          Photo placeholder
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Floating badge */}
                  <div className="absolute -bottom-4 -right-4 bg-slate-800 border border-slate-700 rounded-xl p-4 shadow-xl">
                    <div className="text-teal-400 text-3xl font-bold">20+</div>
                    <div className="text-slate-400 text-sm">Years Experience</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contact" className="relative py-32 overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(${images.heroAlt})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-900" />

        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <span className="text-teal-400 font-medium text-sm tracking-wider uppercase mb-4 block">
            Let&apos;s Talk
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let&apos;s Build Something Real
          </h2>
          <p className="text-slate-400 text-lg mb-6 max-w-2xl mx-auto">
            You&apos;ve proven what&apos;s possible. Now let&apos;s turn it into a product.
          </p>
          <p className="text-slate-400 text-lg mb-12 max-w-2xl mx-auto">
            Book a free 30-minute consultation. I&apos;ll review your project and give you a clear
            picture of what it takes to ship.
          </p>

          <button
            onClick={openCalendly}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-900 font-semibold px-10 py-5 rounded-xl hover:shadow-xl hover:shadow-teal-500/25 transition-all duration-300 hover:scale-105 text-lg cursor-pointer"
          >
            Book Your Free Consultation
            <span>→</span>
          </button>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8 text-slate-500">
            <a
              href="mailto:contact@veedasoftware.com"
              className="hover:text-teal-400 transition-colors"
            >
              contact@veedasoftware.com
            </a>
            <span className="hidden sm:block">•</span>
            <a
              href="https://www.linkedin.com/in/hendrickmelo/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 hover:text-teal-400 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo className="opacity-60" />
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Veeda Software. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a
                href="https://www.linkedin.com/in/hendrickmelo/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-teal-400 transition-colors"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
